// components/CustomerDialog.tsx
import React from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, Select,
    MenuItem, InputLabel, Button, FormControlLabel, Checkbox, FormControl
} from "@mui/material";
import { Customer, User } from "../types";
import { validateEmail, validateDomain } from "../utils/validation";
import { API_BASE_URL } from "../config";
import axiosExt from "../axios";
import { useTranslation } from "react-i18next";
//import { Form } from "react-router-dom";

interface Props {
    open: boolean;
    customer: Customer | null;
    allowSurvey: boolean; 
    onClose: () => void;
    onSaved: (createdCustomer: Customer) => void;
}

const customerActivities = ["direct", "indirect"];
const payTypes = ["Pre-Paid", "Post-Paid"];
const languages = ["en", "de", "pl","ro"];

const CustomerDialog: React.FC<Props> = ({ open, customer, allowSurvey, onClose, onSaved }) => {
    const { t } = useTranslation();
    const [formData, setFormData] = React.useState<Customer>(customer || {
        id: "",
        name: "",
        email: "",
        phone: "",
        webDomain: "",
        accountingCustomerNumber: "",
        activity: "",
        payType: "",
        notes: "",
        language: "en",                              
        satisfactionSurveyUseDefaultSettings: true,  
        satisfactionSurveySendingDays: -1,
        satisfactionSurveySendOnlineReview: null,
        taxInvoiceDueDays: 0,
        serviceInvoiceDueDays: 0,
        assignedToUserId: null,
    });

    const [users, setUsers] = React.useState<User[]>([]);

    const [errorEmail, setErrorEmail] = React.useState(false);
    const [helperEmail, setHelperEmail] = React.useState("");
    const [errorDomain, setErrorDomain] = React.useState(false);
    const [helperDomain, setHelperDomain] = React.useState("");
    const [saving, setSaving] = React.useState(false);

    const fetchUsers = async () => {
        const token = localStorage.getItem("token");
        const response = await axiosExt.get(`${API_BASE_URL}/users`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        console.log("2 Setting users to:", response.data);
        setUsers(response.data);
    };

    React.useEffect(() => {
        if (customer) setFormData(customer);
    }, [customer]);

    React.useEffect(() => {
        fetchUsers();
    }, []);

    const handleSave = async () => {
        if (!validateEmail(formData.email)) {
            setErrorEmail(true);
            setHelperEmail(t("Invalid email format"));
            return;
        }

        if (!validateDomain(formData.webDomain)) {
            setErrorDomain(true);
            setHelperDomain(t("Invalid domain format"));
            return;
        }

        setSaving(true);
        const token = localStorage.getItem("token");

        try {
            const payload = { ...formData };
            if (!payload.id) delete payload.id;

            const method = payload.id ? "put" : "post";
            const url = payload.id
                ? `${API_BASE_URL}/customers/${payload.id}`
                : `${API_BASE_URL}/customers`;

            const response = await axiosExt[method](url, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });

            onSaved(response.data);
            onClose();
        } catch (err) {
            console.error("Error saving customer:", err);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>{formData.id ? t("Edit Customer") : t("Add Customer")}</DialogTitle>
            <DialogContent>
                <TextField label={t("Name")} fullWidth margin="dense"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
                <TextField label={t("Email")} fullWidth margin="dense" error={errorEmail} helperText={helperEmail}
                    value={formData.email}
                    onChange={(e) => {
                        setFormData({ ...formData, email: e.target.value });
                        setErrorEmail(false);
                    }}
                />
                <TextField
                    margin="dense"
                    label={t("Phone")}
                    fullWidth
                    value={formData.phone}
                    onChange={(e) => {
                        setFormData({ ...formData, phone: e.target.value });
                    }}
                />
                <TextField label={t("Web Domain")} fullWidth margin="dense" error={errorDomain} helperText={helperDomain}
                    value={formData.webDomain}
                    onChange={(e) => {
                        setFormData({ ...formData, webDomain: e.target.value });
                        setErrorDomain(false);
                    }}
                />
                <TextField
                    margin="dense"
                    label={t("Accounting #")}
                    type="string"
                    fullWidth
                    value={formData.accountingCustomerNumber}
                    onChange={(e) => { 
                        setFormData({ ...formData, accountingCustomerNumber: e.target.value });
                        
                    }}
                />
                <TextField
                    margin="dense"
                    label={t("Tax Invoice Due Days")}
                    type="number"
                    fullWidth
                    value={formData.taxInvoiceDueDays}
                    onChange={(e) => {
                        const value = e.target.value;
                        setFormData({
                            ...formData,
                            taxInvoiceDueDays: value === "" ? 0 : Number(value),
                        });
                    }}
                />
                <TextField
                    margin="dense"
                    label={t("Service Invoice Due Days")}
                    type="number"
                    fullWidth
                    value={formData.serviceInvoiceDueDays}
                    onChange={(e) => {
                        const value = e.target.value;
                        setFormData({
                            ...formData,
                            serviceInvoiceDueDays: value === "" ? 0 : Number(value),
                        });
                    }}
                />
                <FormControl sx={{ flex: 1 }} margin="dense" fullWidth>
                    <InputLabel id="assigned-label">{t("Assigned To")}</InputLabel>
                    <Select
                        labelId="assigned-label"
                        label={t("Assigned To")}
                        value={formData.assignedToUserId || ""}
                        onChange={(e) => {
                            const newUserId = e.target.value;
                            setFormData({ ...formData, assignedToUserId: newUserId });
                        }}
                    >
                        {[...users].sort((a, b) => a.userName.localeCompare(b.userName)).map((user) => (
                            <MenuItem key={user.id} value={user.id}>
                                {user.userName}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <InputLabel id="select-activtiy">{t("Activity")}</InputLabel>
                <Select
                    labelId="select-activity"
                    fullWidth
                    value={formData.activity}
                    onChange={(e) => {
                        setFormData({ ...formData, activity: e.target.value });
                       }
                    }
                    margin="dense"
                    displayEmpty
                >
                    {customerActivities.map((customerActivity) => (
                        <MenuItem key={customerActivity} value={customerActivity}>
                            {customerActivity}
                        </MenuItem>
                    ))}
                </Select>
                <InputLabel id="select-paytype">{t("Pay Type")}</InputLabel>
                <Select
                    labelId="select-paytype"
                    fullWidth
                    value={formData?.payType || ""}
                    onChange={(e) => {
                        setFormData({ ...formData, payType: e.target.value });
                        }
                    }
                    margin="dense"
                    displayEmpty
                >
                    {payTypes.map((payType) => (
                        <MenuItem key={payType} value={payType}>
                            {payType}
                        </MenuItem>
                    ))}
                </Select>
                <TextField
                    margin="dense"
                    label={t("Notes")}
                    fullWidth
                    multiline
                    value={formData?.notes || ""}
                    onChange={(e) => {
                        setFormData({ ...formData, notes: e.target.value });
                    }}
                />
                <InputLabel id="select-language">{t("Language")}</InputLabel>
                <Select
                    labelId="select-language"
                    fullWidth
                    value={formData.language || "en"}
                    onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                    margin="dense"
                >
                    {languages.map((lang) => (
                        <MenuItem key={lang} value={lang}>
                            {lang.toUpperCase()}
                        </MenuItem>
                    ))}
                </Select>
                {allowSurvey && (
                    <>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formData.satisfactionSurveyUseDefaultSettings ?? false}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            satisfactionSurveyUseDefaultSettings: e.target.checked,
                                        })
                                    }
                                />
                            }
                            label={t("Use Default Survey Settings")}
                        />

                        {/* Survey Sending Days selection */}
                        <InputLabel id="survey-sending-days-label">{t("Survey Sending")}</InputLabel>
                        <Select
                            labelId="survey-sending-days-label"
                            fullWidth
                            value={
                                formData.satisfactionSurveySendingDays === -1
                                    ? "-1"
                                    : formData.satisfactionSurveySendingDays === 0
                                        ? "0"
                                        : "custom"
                            }
                            onChange={(e) => {
                                const value = e.target.value;
                                if (value === "-1") {
                                    setFormData({ ...formData, satisfactionSurveySendingDays: -1 });
                                } else if (value === "0") {
                                    setFormData({ ...formData, satisfactionSurveySendingDays: 0 });
                                } else {
                                    // switch to custom but keep current number if valid
                                    setFormData({
                                        ...formData,
                                        satisfactionSurveySendingDays:
                                            formData.satisfactionSurveySendingDays && formData.satisfactionSurveySendingDays > 0
                                                ? formData.satisfactionSurveySendingDays
                                                : 1,
                                    });
                                }
                            }}
                            margin="dense"
                        >
                            <MenuItem value="-1">{t("Do Not Send")}</MenuItem>
                            <MenuItem value="0">{t("Send for All Orders")}</MenuItem>
                            <MenuItem value="custom">{t("All Orders but No More Than Once per X Days")}</MenuItem>
                        </Select>

                        {/* Only show number input if custom option */}
                        {(formData.satisfactionSurveySendingDays ?? -1) > 0 && (
                                <TextField
                                    margin="dense"
                                    label={t("X Days")}
                                    type="number"
                                    fullWidth
                                    value={formData.satisfactionSurveySendingDays}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            satisfactionSurveySendingDays: e.target.value
                                                ? parseInt(e.target.value, 10)
                                                : 1,
                                        })
                                    }
                                />
                            )}

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formData.satisfactionSurveySendOnlineReview ?? false}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            satisfactionSurveySendOnlineReview: e.target.checked,
                                        })
                                    }
                                />
                            }
                            label={t("Send Online Review")}
                        />
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t("Cancel")}</Button>
                <Button onClick={handleSave} disabled={saving}>{saving ? t("Saving...") : t("Save")}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default CustomerDialog;
